import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, Checkbox, Code, Divider, HStack,
  IconButton, Input, Modal, ModalBody, ModalCloseButton, ModalContent,
  ModalFooter, ModalHeader, ModalOverlay, NumberInput, NumberInputField,
  Select, Table, Tbody, Td, Text, Th, Thead, Tr, VStack, useToast,
  FormControl, FormLabel,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { Trans, useTranslation } from 'react-i18next';

import channexService from '../../services/channexService';

/**
 * Connect an OTA to a Channex property.
 *
 * Follows Channex's flow: pick channel -> render its settings form from the
 * adapter descriptor -> test credentials -> read both sides of the mapping ->
 * build rate_plans mappings -> create (inactive) -> check readiness ->
 * activate.
 *
 * Both forms are rendered from the descriptor (`params` for the connection,
 * `rate_params` per mapping) rather than hardcoded per channel, because every
 * OTA asks for different fields. Activation stays a separate, explicit step:
 * it full-syncs to the OTA and makes inventory publicly bookable.
 */

// Names that suggest a credential — masked in the form. Channex descriptors
// have no password type, so this is a heuristic, not a contract.
const SECRET_HINT = /(password|secret|token|api_key|apikey)/i;

let rowSeq = 1;

const ConnectChannelModal = ({ isOpen, onClose, config, onDone }) => {
  const [channels, setChannels] = useState([]);
  const [code, setCode] = useState('');
  const [adapter, setAdapter] = useState(null);
  const [settings, setSettings] = useState({});
  const [tested, setTested] = useState(null);
  const [mapping, setMapping] = useState(null);   // {channel, ours} from mapping-options
  const [rows, setRows] = useState([]);           // mapping rows being built
  const [created, setCreated] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [connections, setConnections] = useState([]);
  const toast = useToast();
  const { t } = useTranslation();

  const configId = config?.id;

  const loadConnections = useCallback(async () => {
    if (!configId) return;
    try {
      const data = await channexService.connections(configId);
      setConnections(data.connections || []);
    } catch (err) {
      /* listing is informational; failure shouldn't block connecting */
    }
  }, [configId]);

  useEffect(() => {
    if (!isOpen || !configId) return;
    setCode(''); setAdapter(null); setSettings({}); setTested(null);
    setMapping(null); setRows([]); setCreated(null); setReadiness(null); setTitle('');
    channexService.availableChannels(configId)
      .then((d) => setChannels(d.channels || []))
      .catch((err) => toast({ title: t('channexConnect.toasts.loadChannelsFailed'), description: err.message, status: 'error' }));
    loadConnections();
  }, [isOpen, configId, loadConnections, toast, t]);

  const pickChannel = async (value) => {
    setCode(value); setAdapter(null); setSettings({}); setTested(null);
    setMapping(null); setRows([]); setCreated(null); setReadiness(null);
    if (!value) return;
    setBusy(true);
    try {
      const descriptor = await channexService.channelAdapter(configId, value);
      setAdapter(descriptor);
      // Pre-seed descriptor defaults so booleans like
      // send_email_notifications carry their documented default.
      const seeded = {};
      Object.entries(descriptor.params || {}).forEach(([name, spec]) => {
        if (spec && spec.default !== undefined) seeded[name] = spec.default;
      });
      setSettings(seeded);
    } catch (err) {
      toast({ title: t('channexConnect.toasts.loadSettingsFailed'), description: err.message, status: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const run = async (label, fn, after) => {
    setBusy(true);
    try {
      const data = await fn();
      if (data.success === false) {
        toast({ title: t('channexConnect.toasts.actionFailed', { action: label }), description: data.message, status: 'warning', duration: 8000 });
      } else {
        toast({ title: t('channexConnect.toasts.actionOk', { action: label }), description: data.message, status: 'success', duration: 5000 });
        after?.(data);
      }
      return data;
    } catch (err) {
      toast({
        title: t('channexConnect.toasts.actionFailed', { action: label }),
        description: err.response?.data?.detail
          || err.response?.data?.message || err.message,
        status: 'error', duration: 8000,
      });
    } finally {
      setBusy(false);
    }
  };

  const loadMappingOptions = async () => {
    await run(t('channexConnect.actions.loadMappingOptions'),
      () => channexService.mappingOptions(configId, code, settings),
      (d) => { setMapping(d.data || null); setRows([]); });
  };

  // ------------------------------------------------------------- mapping

  const ourOptions = mapping?.ours?.rate_plan_options?.length
    ? mapping.ours.rate_plan_options
    : (mapping?.ours?.rate_plans || []);
  const channelRooms = Array.isArray(mapping?.channel?.rooms) ? mapping.channel.rooms : [];
  const rateParams = adapter?.rate_params || {};
  const hasRateParams = Object.keys(rateParams).length > 0;

  const addRow = () => {
    const seeded = {};
    // pricing_type is reported once per hotel in the mapping details —
    // copy it rather than asking the operator to know it.
    if ('pricing_type' in rateParams && mapping?.channel?.pricing_type) {
      seeded.pricing_type = mapping.channel.pricing_type;
    }
    setRows((r) => [...r, { key: rowSeq++, ratePlanId: '', settings: seeded }]);
  };

  const dropRow = (key) => setRows((r) => r.filter((row) => row.key !== key));

  const setRow = (key, patch) =>
    setRows((r) => r.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  const setRowSetting = (key, name, value) =>
    setRows((r) => r.map((row) => {
      if (row.key !== key) return row;
      const next = { ...row.settings, [name]: value };
      // Channel-side hierarchy: changing the room invalidates the rate,
      // changing the rate invalidates the occupancy.
      if (name === 'room_type_code') { delete next.rate_plan_code; delete next.occupancy; }
      if (name === 'rate_plan_code') {
        delete next.occupancy;
        const room = channelRooms.find((rm) => String(rm.id) === String(next.room_type_code));
        const rate = (room?.rates || []).find((rt) => String(rt.id) === String(value));
        if (rate && 'readonly' in rateParams) next.readonly = Boolean(rate.readonly);
      }
      return { ...row, settings: next };
    }));

  const roomFor = (row) =>
    channelRooms.find((rm) => String(rm.id) === String(row.settings.room_type_code));
  const rateFor = (row) =>
    (roomFor(row)?.rates || []).find((rt) => String(rt.id) === String(row.settings.rate_plan_code));

  const rateParamField = (row, name, spec) => {
    const value = row.settings[name] ?? '';
    const label = spec.title || name;

    // The common channel shape (Booking.com and friends) exposes
    // rooms -> rates -> occupancies; offer dropdowns when we have it.
    if (name === 'room_type_code' && channelRooms.length) {
      return (
        <Select key={name} size="xs" placeholder={label} value={value}
                onChange={(e) => setRowSetting(row.key, name, e.target.value)}>
          {channelRooms.map((rm) => (
            <option key={rm.id} value={rm.id}>{rm.title || rm.id}</option>
          ))}
        </Select>
      );
    }
    if (name === 'rate_plan_code' && channelRooms.length) {
      const rates = roomFor(row)?.rates || [];
      return (
        <Select key={name} size="xs" placeholder={label} value={value}
                isDisabled={!rates.length}
                onChange={(e) => setRowSetting(row.key, name, e.target.value)}>
          {rates.map((rt) => (
            <option key={rt.id} value={rt.id}>{rt.title || rt.id}</option>
          ))}
        </Select>
      );
    }
    if (name === 'occupancy' && (rateFor(row)?.occupancies || []).length) {
      return (
        <Select key={name} size="xs" placeholder={label} value={value}
                onChange={(e) => setRowSetting(row.key, name, e.target.value)}>
          {rateFor(row).occupancies.map((o) => (
            <option key={o} value={o}>{label}: {o}</option>
          ))}
        </Select>
      );
    }
    if (spec.type === 'select' && Array.isArray(spec.options)) {
      return (
        <Select key={name} size="xs" placeholder={label} value={value}
                onChange={(e) => setRowSetting(row.key, name, e.target.value)}>
          {spec.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </Select>
      );
    }
    if (spec.type === 'boolean') {
      return (
        <Checkbox key={name} size="sm" isChecked={Boolean(value)}
          onChange={(e) => setRowSetting(row.key, name, e.target.checked)}>
          <Text fontSize="xs">{label}</Text>
        </Checkbox>
      );
    }
    if (spec.type === 'integer') {
      return (
        <NumberInput key={name} size="xs" value={value}
          onChange={(v) => setRowSetting(row.key, name, v)}>
          <NumberInputField placeholder={label} />
        </NumberInput>
      );
    }
    return (
      <Input key={name} size="xs" placeholder={label} value={value}
        onChange={(e) => setRowSetting(row.key, name, e.target.value)} />
    );
  };

  // Build the rate_plans entries Channex expects:
  // {rate_plan_id, settings: {...rate_params...}} — integers coerced, and
  // exactly one mapping per channel room+rate pair marked primary when the
  // adapter uses primary_occ and the operator didn't mark one.
  const buildMappings = () => {
    const built = rows
      .filter((row) => row.ratePlanId)
      .map((row) => {
        const s = {};
        Object.entries(rateParams).forEach(([name, spec]) => {
          let v = row.settings[name];
          if (v === '' || v === undefined || v === null) return;
          if (spec.type === 'integer' || name === 'occupancy'
              || name === 'room_type_code' || name === 'rate_plan_code') {
            const n = Number(v);
            v = Number.isNaN(n) ? v : n;
          }
          s[name] = v;
        });
        return { rate_plan_id: row.ratePlanId, settings: s };
      });

    if ('primary_occ' in rateParams) {
      const pairsWithPrimary = new Set(
        built.filter((m) => m.settings.primary_occ)
          .map((m) => `${m.settings.room_type_code}:${m.settings.rate_plan_code}`)
      );
      const seen = new Set();
      built.forEach((m) => {
        const pair = `${m.settings.room_type_code}:${m.settings.rate_plan_code}`;
        if (!pairsWithPrimary.has(pair) && !seen.has(pair)) {
          m.settings.primary_occ = true;
        }
        seen.add(pair);
      });
    }
    return built;
  };

  const mappingsReady = rows.length > 0 && rows.every((row) => row.ratePlanId);

  // ------------------------------------------------------------- settings

  const field = (name, spec) => {
    const value = settings[name] ?? '';
    if (spec.type === 'boolean') {
      return (
        <Checkbox key={name} size="sm" isChecked={Boolean(value)}
          onChange={(e) => setSettings((s) => ({ ...s, [name]: e.target.checked }))}>
          {spec.title || name}
        </Checkbox>
      );
    }
    if (spec.type === 'select' && Array.isArray(spec.options)) {
      return (
        <FormControl key={name}>
          <FormLabel fontSize="sm">{spec.title || name}</FormLabel>
          <Select size="sm" placeholder="—" value={value}
            onChange={(e) => { setSettings((s) => ({ ...s, [name]: e.target.value })); setTested(null); }}>
            {spec.options.map((o) => <option key={o} value={o}>{o}</option>)}
          </Select>
        </FormControl>
      );
    }
    return (
      <FormControl key={name}>
        <FormLabel fontSize="sm">{spec.title || name}</FormLabel>
        <Input size="sm" value={value}
          type={SECRET_HINT.test(name) ? 'password' : (spec.type === 'integer' ? 'number' : 'text')}
          onChange={(e) => { setSettings((s) => ({ ...s, [name]: e.target.value })); setTested(null); }} />
      </FormControl>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('channexConnect.title', { hotel: config?.hotel?.name })}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {connections.length > 0 && (
              <Box>
                <Text fontSize="xs" color="gray.500" mb={1}>{t('channexConnect.connections.title')}</Text>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>{t('channexConnect.connections.channel')}</Th>
                      <Th>{t('channexConnect.connections.state')}</Th>
                      <Th>{t('channexConnect.connections.active')}</Th>
                      <Th></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {connections.map((c) => (
                      <Tr key={c.id}>
                        <Td>{c.title || c.channel}</Td>
                        <Td><Badge>{c.state || '—'}</Badge></Td>
                        <Td>
                          <Badge colorScheme={c.is_active ? 'green' : 'gray'}>
                            {c.is_active ? t('channexConnect.connections.yes') : t('channexConnect.connections.no')}
                          </Badge>
                        </Td>
                        <Td>
                          <HStack spacing={1} justify="flex-end">
                            <Button size="xs" variant="ghost" isLoading={busy}
                              onClick={() => run(t('channexConnect.actions.readinessCheck'),
                                () => channexService.channelAction(configId, c.id, 'check_readiness'))}>
                              {t('channexConnect.connections.readiness')}
                            </Button>
                            <Button size="xs" variant="outline" isLoading={busy}
                              onClick={() => run(
                                c.is_active ? t('channexConnect.actions.deactivate') : t('channexConnect.actions.activate'),
                                () => channexService.channelAction(
                                  configId, c.id, c.is_active ? 'deactivate' : 'activate'),
                                loadConnections,
                              )}>
                              {c.is_active ? t('channexConnect.actions.deactivate') : t('channexConnect.actions.activate')}
                            </Button>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                <Divider my={3} />
              </Box>
            )}

            <FormControl>
              <FormLabel fontSize="sm">{t('channexConnect.form.channel')}</FormLabel>
              <Select size="sm" placeholder={t('channexConnect.form.selectOta')} value={code}
                      onChange={(e) => pickChannel(e.target.value)}>
                {channels.map((c) => (
                  <option key={c.code} value={c.code}>{c.title}</option>
                ))}
              </Select>
            </FormControl>

            {adapter && (
              <Box borderWidth="1px" borderRadius="md" p={3}>
                <Text fontSize="xs" color="gray.500" mb={2}>
                  {t('channexConnect.form.adapterIntro', { channel: adapter.title })}
                </Text>
                <VStack align="stretch" spacing={3}>
                  <FormControl>
                    <FormLabel fontSize="sm">{t('channexConnect.form.connectionTitle')}</FormLabel>
                    <Input size="sm" value={title} placeholder={t('channexConnect.form.connectionTitlePlaceholder')}
                      onChange={(e) => setTitle(e.target.value)} />
                  </FormControl>
                  {Object.entries(adapter.params || {}).map(([n, s]) => field(n, s))}
                  {Object.keys(adapter.params || {}).length === 0 && (
                    <Text fontSize="sm" color="gray.500">
                      {t('channexConnect.form.noSettings')}
                    </Text>
                  )}
                </VStack>
              </Box>
            )}

            {tested?.success && (
              <Alert status="success" borderRadius="md" fontSize="sm">
                <AlertIcon />{t('channexConnect.alerts.credentialsAccepted')}
              </Alert>
            )}

            {mapping && hasRateParams && (
              <Box borderWidth="1px" borderRadius="md" p={3}>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="xs" color="gray.500">
                    {t('channexConnect.mappings.intro')}
                  </Text>
                  <Button size="xs" onClick={addRow}>{t('channexConnect.mappings.addMapping')}</Button>
                </HStack>
                {!ourOptions.length && (
                  <Alert status="warning" borderRadius="md" fontSize="xs" mb={2}>
                    <AlertIcon />{t('channexConnect.mappings.noRatePlans')}
                  </Alert>
                )}
                <VStack align="stretch" spacing={2}>
                  {rows.map((row) => (
                    <HStack key={row.key} align="center" spacing={2}>
                      <Select size="xs" flex="1.4" placeholder={t('channexConnect.mappings.yourRatePlan')}
                        value={row.ratePlanId}
                        onChange={(e) => setRow(row.key, { ratePlanId: e.target.value })}>
                        {ourOptions.map((o) => (
                          <option key={o.channex_id} value={o.channex_id}>
                            {o.occupancy
                              ? t('channexConnect.mappings.ratePlanWithOccupancy', { title: o.title, occupancy: o.occupancy })
                              : o.title}
                          </option>
                        ))}
                      </Select>
                      {Object.entries(rateParams).map(([n, s]) => (
                        <Box key={n} flex="1">{rateParamField(row, n, s)}</Box>
                      ))}
                      <IconButton aria-label={t('channexConnect.mappings.removeMapping')} icon={<CloseIcon />}
                        size="xs" variant="ghost" onClick={() => dropRow(row.key)} />
                    </HStack>
                  ))}
                  {rows.length === 0 && (
                    <Text fontSize="xs" color="gray.500">
                      {t('channexConnect.mappings.empty')}
                    </Text>
                  )}
                </VStack>
              </Box>
            )}

            {created?.channel_id && (
              <Alert status="info" borderRadius="md" fontSize="sm">
                <AlertIcon />
                <Box>
                  <Trans i18nKey="channexConnect.alerts.createdInactive"
                    values={{ id: created.channel_id }}
                    components={{ code: <Code fontSize="xs" /> }} />
                </Box>
              </Alert>
            )}

            {readiness && (
              <Alert status={readiness.success === false ? 'warning' : 'success'}
                     borderRadius="md" fontSize="sm">
                <AlertIcon />{readiness.message || t('channexConnect.alerts.readinessChecked')}
              </Alert>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={onClose}>{t('channexConnect.buttons.close')}</Button>
          <Button size="sm" isDisabled={!code} isLoading={busy}
            onClick={() => run(t('channexConnect.actions.testConnection'),
              () => channexService.testChannel(configId, code, settings),
              (d) => {
                setTested(d);
                // Read both sides of the mapping as soon as the credentials
                // are known-good.
                channexService.mappingOptions(configId, code, settings)
                  .then((m) => { if (m.success !== false) { setMapping(m.data || null); setRows([]); } })
                  .catch(() => {});
              })}>
            {t('channexConnect.buttons.testCredentials')}
          </Button>
          {tested?.success && !mapping && (
            <Button size="sm" variant="outline" isLoading={busy} onClick={loadMappingOptions}>
              {t('channexConnect.buttons.loadMappings')}
            </Button>
          )}
          <Button size="sm" colorScheme="blue" isLoading={busy}
            isDisabled={!code || created || (hasRateParams && mapping && rows.length > 0 && !mappingsReady)}
            onClick={() => {
              const mappings = mapping ? buildMappings() : [];
              if (hasRateParams && mappings.length === 0) {
                toast({
                  title: t('channexConnect.toasts.noMappingsTitle'),
                  description: t('channexConnect.toasts.noMappingsDescription'),
                  status: 'warning', duration: 6000,
                });
              }
              run(t('channexConnect.actions.createConnection'),
                () => channexService.createChannel(configId, {
                  code, settings, mappings, title: title || undefined,
                }),
                (d) => { setCreated(d); loadConnections(); });
            }}>
            {t('channexConnect.buttons.createInactive')}
          </Button>
          {created?.channel_id && (
            <Button size="sm" variant="outline" isLoading={busy}
              onClick={async () => {
                const d = await run(t('channexConnect.actions.readinessCheck'),
                  () => channexService.channelAction(configId, created.channel_id, 'check_readiness'));
                if (d) setReadiness(d);
              }}>
              {t('channexConnect.buttons.checkReadiness')}
            </Button>
          )}
          {created?.channel_id && (
            <Button size="sm" colorScheme="orange" isLoading={busy}
              onClick={() => run(t('channexConnect.actions.activate'),
                () => channexService.channelAction(configId, created.channel_id, 'activate'),
                () => { loadConnections(); onDone?.(); })}>
              {t('channexConnect.actions.activate')}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ConnectChannelModal;
